import { useEffect, useState } from 'react';

import debounce from '@material-ui/core/utils/debounce';

import { useStores } from 'hooks/use-stores';

interface Props {
    eventTicker: string;
    marketTicker: string;
    forceLoad?: boolean;
    forceUpdate?: boolean;
    dontDispose?: boolean;
    currentPositionYes?: number;
    currentPositionNo?: number;
}

interface Return {
    yesPayout: number | undefined;
    noPayout: number | undefined;
    sellYesPayout: number | undefined;
    sellNoPayout: number | undefined;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    isOrderbookLoaded: boolean;
    isPayoutLoaded: boolean;
}

export default function useHoverPayoutText({
    eventTicker,
    marketTicker,
    forceLoad,
    forceUpdate,
    dontDispose,
    currentPositionYes,
    currentPositionNo,
}: Props): Return {
    const { marketStore } = useStores();

    const [isHovering, setIsHovering] = useState(!!forceLoad);
    // debounce the hovering state to prevent too many calls to loadOrderBookFromREST (429 error)
    const debouncedSetIsHovering = debounce(setIsHovering, 300);
    const [isPayoutLoaded, setIsPayoutLoaded] = useState(false);

    const onMouseEnter = () => {
        debouncedSetIsHovering(true);
    };
    const onMouseLeave = () => {
        debouncedSetIsHovering.clear();
        setIsHovering(false);
    };

    const event = marketStore.resolveEvent(eventTicker);

    useEffect(
        () => {
            if (isHovering && !isPayoutLoaded && !event.loaded && !!eventTicker) {
                marketStore.loadEvent(eventTicker);
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isHovering],
    );

    const targetMarket = event.markets.find((m) => m.ticker === marketTicker);
    const orderbook = marketStore.resolveOrderBook(targetMarket ? targetMarket.id : '');
    // TODO: Remove this
    useEffect(
        () => {
            if (event.loaded && event.seriesTicker) {
                const targetMarket = event.markets.find((m) => m.ticker === marketTicker);

                const loadOrderbook = async () => {
                    if (!!targetMarket?.id && event.seriesTicker && !orderbook.wsSubscribed && !orderbook.polling) {
                        // load from rest because web sockets will not properly load often times
                        orderbook.loadOrderBook(targetMarket.ticker);
                    }
                };

                const disposeOrderbook = () => {
                    if (orderbook.loaded && !dontDispose) {
                        orderbook.dispose();
                    }
                };

                if (!targetMarket) {
                    return () => disposeOrderbook();
                }

                if ((isHovering && !isPayoutLoaded) || forceUpdate) {
                    loadOrderbook();
                }

                return () => {
                    disposeOrderbook();
                };
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [event.loaded, isHovering, marketTicker, forceUpdate],
    );

    const [yesPayout, setYesPayout] = useState<number | undefined>();
    const [noPayout, setNoPayout] = useState<number | undefined>();
    const [sellYesPayout, setSellYesPayout] = useState<number | undefined>();
    const [sellNoPayout, setSellNoPayout] = useState<number | undefined>();

    useEffect(
        () => {
            if (event.loaded && orderbook?.loaded) {
                const orderCost = 10000; // $100
                const [yesQuantity, yesCostCents] = orderbook.getQuantityAndCostForMaxCostInCents(
                    orderCost,
                    true,
                    event.seriesTicker,
                );
                const [noQuantity, noCostCents] = orderbook.getQuantityAndCostForMaxCostInCents(
                    orderCost,
                    false,
                    event.seriesTicker,
                );

                if (yesQuantity > 0) {
                    setYesPayout(yesQuantity + 100 - yesCostCents / 100);
                } else {
                    setYesPayout(0);
                }

                if (noQuantity > 0) {
                    setNoPayout(noQuantity + 100 - noCostCents / 100);
                } else {
                    setNoPayout(0);
                }

                if (!!currentPositionYes) {
                    const sellYesCost = orderbook.getCostInCentsForQuantity(
                        currentPositionYes,
                        false,
                        event.seriesTicker,
                    );
                    const estimatedSellYesPayout = currentPositionYes - sellYesCost / 100;
                    if (estimatedSellYesPayout > 0) {
                        setSellYesPayout(estimatedSellYesPayout);
                    }
                }

                if (!!currentPositionNo) {
                    const sellNoCost = orderbook.getCostInCentsForQuantity(currentPositionNo, true, event.seriesTicker);
                    const estimatedSellNoPayout = currentPositionNo - sellNoCost / 100;
                    if (estimatedSellNoPayout > 0) {
                        setSellNoPayout(estimatedSellNoPayout);
                    }
                }

                setIsPayoutLoaded(true);
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [event, orderbook.updatedTs, marketTicker],
    );

    return {
        yesPayout,
        noPayout,
        sellYesPayout,
        sellNoPayout,
        onMouseEnter,
        onMouseLeave,
        isOrderbookLoaded: orderbook.loaded,
        isPayoutLoaded,
    };
}
